import type { ImageSegmenter } from '@mediapipe/tasks-vision';

/**
 * Replaces the background of a captured customer photo with a flat colour,
 * entirely on the device.
 *
 * ⚠️ **This has to run in the browser, not the API.** The photo reaches R2 by a
 * presigned PUT straight from the device, and Gemini fetches it from R2 itself —
 * the API server only ever holds the object *key*, never the bytes (see
 * `tryon-service-endpoints.md` § Key Decisions). Segmenting server-side would
 * put the image back through our own machine and undo that, so the work happens
 * here, between picking the file and uploading it.
 *
 * **Why a flat fill and not transparency.** A transparent PNG has to be
 * flattened by whatever decodes it upstream, commonly onto black, which draws a
 * dark halo around exactly the subject we isolated. Compositing here means what
 * we write is what Gemini sees. It also keeps the output `image/jpeg`, so the
 * type signed into the presigned url (G39) is unchanged, and a re-encoded photo
 * stays ~2-3MB rather than the ~15MB the same pixels cost as PNG.
 *
 * **Every failure path returns null, and null means "upload the original".**
 * Background removal is an improvement to the photo, not a precondition for
 * trying a garment on — a device that cannot run the model must still be able
 * to complete a visit.
 */

/**
 * Served from our own origin, not a CDN. The customer path should not acquire a
 * third-party runtime dependency, and the files are cached by the service
 * worker on first use — see the `mediapipe-assets` rule in `vite.config.ts`.
 */
const WASM_BASE = '/mediapipe';
const MODEL_PATH = '/mediapipe/selfie_segmenter.tflite';

/**
 * The colour the background becomes. A light neutral grey rather than white:
 * white silently merges with a pale kurta or dupatta at the mask's soft edge,
 * and the seam then reads as a hole in the garment.
 */
const BACKDROP = '#f2f2f2';

/**
 * 0.92 rather than the browser default 0.92-ish-but-unspecified. Stated so the
 * output size is predictable, and high enough that JPEG ringing does not add
 * texture to a perfectly flat backdrop.
 */
const JPEG_QUALITY = 0.92;

/**
 * ⚠️ **Built once per page load, not once per photo.** `createFromOptions`
 * downloads ~12MB of wasm and a 250KB model and stands up a WebGL context;
 * doing that per capture would put several seconds in front of every retake.
 *
 * Held as the *promise* so two overlapping calls share one initialisation, and
 * cleared on failure so a device that was offline for the first attempt can try
 * again rather than being poisoned for the rest of the session.
 */
let segmenterPromise: Promise<ImageSegmenter> | null = null;

function loadSegmenter(): Promise<ImageSegmenter> {
  if (segmenterPromise) return segmenterPromise;

  segmenterPromise = (async () => {
    /**
     * ⚠️ **Imported dynamically, and it matters.** A static import puts
     * MediaPipe's 155KB bundle into the entry chunk, where every admin loading
     * the dashboard pays for a segmenter they will never reach. As a dynamic
     * import Rollup splits it into its own chunk, fetched by `warmSegmenter`
     * when the capture screen actually opens.
     */
    const { FilesetResolver, ImageSegmenter } = await import('@mediapipe/tasks-vision');

    const fileset = await FilesetResolver.forVisionTasks(WASM_BASE);
    return ImageSegmenter.createFromOptions(fileset, {
      /**
       * ⚠️ **CPU, not GPU, and deliberately.** GPU is the right delegate for
       * video, where the same model runs 30 times a second; this runs it *once*
       * on a still frame, where the difference is a couple of hundred
       * milliseconds against a generation the customer is about to wait ~55
       * seconds for. What GPU adds is a WebGL context that has to be acquired
       * and can be lost — and because every failure here degrades silently to
       * "upload the original", a device with flaky WebGL would simply stop
       * removing backgrounds with nothing on screen to say so.
       */
      baseOptions: { modelAssetPath: MODEL_PATH, delegate: 'CPU' },
      runningMode: 'IMAGE',
      /**
       * ⚠️ **Confidence, not category.** The model's embedded `labels.txt` holds
       * a single label — `selfie` — so it emits one channel, not a two-class
       * map, and `categoryMask` on a one-class model is degenerate. Each pixel
       * of `confidenceMasks[0]` is the probability that the pixel is the person,
       * which is already an alpha channel and carries soft edges through hair
       * instead of a binary cut.
       */
      outputConfidenceMasks: true,
      outputCategoryMask: false,
    });
  })();

  segmenterPromise.catch(() => {
    segmenterPromise = null;
  });

  return segmenterPromise;
}

/**
 * Warms the model without producing an image. Call it when the capture screen
 * mounts so the download overlaps the customer framing their shot, rather than
 * landing after they press the shutter. Safe to call repeatedly; never throws.
 */
export function warmSegmenter(): void {
  void loadSegmenter().catch(() => {
    // Deliberately silent — this is an optimisation, and `cleanBackground` will
    // report the same failure properly if it turns out to matter.
  });
}

/**
 * Returns a JPEG with the background replaced, or **null** if this device or
 * this file cannot be segmented — in which case the caller uploads the original
 * untouched.
 *
 * ⚠️ **HEIC is the expected null on non-Apple platforms.** `createImageBitmap`
 * cannot decode it outside Safari, for the same reason `<img>` cannot, which
 * `CaptureScreen` already accounts for in `isPreviewable`. iOS Safari usually
 * hands over a transcoded JPEG anyway, so this mostly costs nothing.
 */
export async function cleanBackground(file: File): Promise<Blob | null> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return null;
  }

  try {
    const segmenter = await loadSegmenter();
    const result = segmenter.segment(bitmap);
    const mask = result.confidenceMasks?.[0];
    if (!mask) {
      result.close();
      return null;
    }

    /**
     * The mask comes back at the model's own 256x256, not the photo's size. It
     * is painted into a canvas of *its* dimensions and then drawn scaled, so the
     * 2D compositor does the upscale — bilinear and on the GPU, where doing it
     * by hand would be a nested loop over several million pixels on the main
     * thread.
     *
     * ⚠️ **`result.close()` happens after this block, not before it.**
     * `getAsFloat32Array` returns the *underlying* mask rather than a copy, so
     * closing the result first would leave this loop reading a buffer MediaPipe
     * has already released — which does not throw, it just yields a garbage
     * mask on some runs and a correct one on others.
     */
    const maskCanvas = new OffscreenCanvas(mask.width, mask.height);
    const maskCtx = maskCanvas.getContext('2d');
    if (!maskCtx) {
      result.close();
      return null;
    }

    const confidence = mask.getAsFloat32Array();
    const maskImage = maskCtx.createImageData(mask.width, mask.height);
    for (let i = 0; i < confidence.length; i++) {
      // RGB is irrelevant under `destination-in`; only alpha is read.
      maskImage.data[i * 4 + 3] = (confidence[i] ?? 0) * 255;
    }
    maskCtx.putImageData(maskImage, 0, 0);
    result.close();

    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(bitmap, 0, 0);
    // Keep the photo only where the mask says "person".
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(maskCanvas, 0, 0, bitmap.width, bitmap.height);
    // Then slide the flat colour in behind what survived.
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = BACKDROP;
    ctx.fillRect(0, 0, bitmap.width, bitmap.height);

    return await canvas.convertToBlob({ type: 'image/jpeg', quality: JPEG_QUALITY });
  } catch {
    return null;
  } finally {
    bitmap.close();
  }
}
